import { EditOutlined, PlusOutlined, StopOutlined, ZoomInOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { Badge, Button } from 'antd';
import theme from 'shared/constants/theme.json';
import Page from 'components/Page';
import StatusTag from 'components/StatusTag';
import Table from 'components/Table';
import { useMutation } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import React, { useCallback, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';
import { reachedLicenseLimit } from 'shared/utils/license';
import EditHeatmap from './EditHeatmap';

const extraFields = `
  website {
    id
    matomoId
    matomoHeatmaps {
      id
      name
      createdAt
      status
      sampleRate
      lastSample
      rules {
        attribute
        type
        value
        value2
        inverted
      }
      excludedElements
      breakpointMobile
      breakpointTablet
      samples
      matomoHsrId
    }
  }
`;

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    type: 'date',
    defaultSortOrder: 'descend',
  },
  {
    title: 'Sample rate',
    dataIndex: 'sampleRate',
    render: (value) => `${value}%`,
    type: 'number',
  },
  {
    title: 'Samples',
    dataIndex: 'samples',
    type: 'number',
  },
  {
    title: 'Last sample',
    dataIndex: ['lastSample', 0],
    type: 'relativeDate',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: status => <StatusTag status={status} />,
    align: 'center',
  },
];

const viewPath = '/data-sources/:dataSourceId/hsr/heatmaps/:matomoHsrId';

const STOP_HEATMAP = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!) {
    stopMatomoHeatmap(websiteId: $websiteId, matomoHsrId: $matomoHsrId)
  }
`;

const HeatmapList = () => {
  const { dataSource, dataSourceId, loading, getDataSourceLicenseSetting, refetch } = useDataSource({ extraFields });
  const [editHeatmap, setEditHeatmap] = useState(false);
  const [stopHeatmap, { loading: stopping }] = useMutation(STOP_HEATMAP);

  const handleStop = async (item) => {
    await stopHeatmap({
      variables: {
        websiteId: dataSource.website.id,
        matomoHsrId: item.matomoHsrId,
      },
    });
    refetch();
  };

  const handleDone = () => {
    setEditHeatmap(false);
    refetch();
  };

  const heatmaps = dataSource?.website?.matomoHeatmaps || [];
  const limit = getDataSourceLicenseSetting('heatmapLimit');
  const canCreate = !reachedLicenseLimit(heatmaps.length, limit);

  const actionsMenuItems = (record) => [
    {
      danger: true,
      key: 'stop',
      label: 'Stop',
      icon: <StopOutlined />,
      onClick: () => handleStop(record),
      visible: record.status !== 'ENDED',
    },
  ];

  const actions = useCallback((record) => [
    <Link key="results" to={generatePath(viewPath, { dataSourceId, matomoHsrId: record.matomoHsrId })}>
      <Button icon={<ZoomInOutlined />}>View</Button>
    </Link>,
    <Button key="edit" onClick={() => setEditHeatmap(record)} icon={<EditOutlined />}>Edit</Button>,
  ], []);

  return (
    <Page>
      <Page.Header
        title="Heatmaps"
        extra={[
          <Badge key="usage" style={{ backgroundColor: theme[canCreate ? 'success-color' : 'error-color'] }} count={`${heatmaps.length || 0} / ${limit >= 0 ? limit : '∞'}`} showZero />,
          <Button key="create-heatmap" style={{ margin: '1em 0' }} block type="primary" onClick={() => setEditHeatmap(true)} icon={<PlusOutlined />} disabled={!canCreate} className="create-heatmap">Create heatmap</Button>,
        ]}
      />
      <Page.Content>
        <Table loading={loading || stopping} dataSource={heatmaps} columns={columns} actions={actions} actionsMenuItems={actionsMenuItems} />
        <EditHeatmap heatmap={editHeatmap} open={!!editHeatmap} onCancel={() => setEditHeatmap(false)} onDone={handleDone} websiteId={dataSource?.website.id} />
      </Page.Content>
    </Page>
  );
};

export default HeatmapList;
